import React from "react";
import {
  Box,
  Link,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import chevronTeal from "@assets/facilityNetwork/lf-chevron-teal.svg";
import {
  paginateLinkedFacilityRows,
  linkedFacilityStatusDisplay,
  type ChildFacilityStatus,
  type LinkedFacilityRow,
  type LinkedFacilitySortKey,
  type RosterFacilityStatus,
} from "./facilityNetworkChildrenUtils";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import { FN } from "./facilityNetworkTheme";

export const LINKED_FACILITY_ROWS_PER_PAGE_OPTIONS = [5, 10, 20] as const;
export const LINKED_FACILITY_DEFAULT_ROWS_PER_PAGE = 10;
/** `rowsPerPage` value meaning "Show all" (no pagination). */
export const LINKED_FACILITY_SHOW_ALL = 0;

type Props = {
  rows: LinkedFacilityRow[];
  filteredCount: number;
  sortKey: LinkedFacilitySortKey;
  sortDir: "asc" | "desc";
  onSortChange: (key: LinkedFacilitySortKey) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onViewDashboard?: (facilityId: number) => void;
  onRemove?: (facilityId: number) => void;
  loading?: boolean;
};

const headerCellSx = {
  fontFamily: FN.fontFamily,
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  borderBottom: "none",
  borderRight: `1px solid ${FN.figmaCardBorder}`,
  py: 0,
  px: 1.5,
  height: 40,
  lineHeight: "20px",
};

const bodyCellSx = {
  fontFamily: FN.fontFamily,
  fontSize: 12,
  letterSpacing: "0.03px",
  color: FN.muted,
  borderBottom: `1px solid ${FN.figmaCardBorder}`,
  borderRight: `1px solid ${FN.figmaCardBorder}`,
  py: 0,
  px: 1.5,
  height: 48,
  verticalAlign: "middle" as const,
};

const STATUS_PILL: Record<
  ChildFacilityStatus | RosterFacilityStatus,
  { bg: string; border: string; text: string }
> = {
  /* Figma 9142:99091 status pills */
  active: { bg: "#dff6dd", border: "#8dc88d", text: "#107c10" },
  recently_linked: { bg: "#dff6dd", border: "#8dc88d", text: "#107c10" },
  linked: { bg: "#dff6dd", border: "#8dc88d", text: "#107c10" },
  pending: { bg: "#fff4e5", border: "#fabc89", text: FN.figmaWarning },
  declined: { bg: FN.errorBg, border: FN.errorBorder, text: FN.figmaError },
  cancelled: { bg: "#f3f2f1", border: "#c8c6c4", text: "#605e5c" },
  invite: { bg: "#fff4e5", border: "#fabc89", text: FN.figmaWarning },
};

function StatusPill({ row }: { row: LinkedFacilityRow }) {
  const label = linkedFacilityStatusDisplay(row);
  const colors = STATUS_PILL[row.status === "linked" ? "linked" : row.status];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "9px",
        py: "3px",
        borderRadius: "68px",
        bgcolor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        fontSize: 12,
        fontWeight: 400,
        letterSpacing: "0.03px",
        lineHeight: "16px",
        fontFamily: FN.fontFamily,
      }}
    >
      {label}
    </Box>
  );
}

function TypeCell({ label }: { label: string }) {
  if (!label || label === "—") {
    return (
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          fontWeight: 400,
          color: FN.figmaAccent,
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        —
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "8px",
        py: "2px",
        borderRadius: "8px",
        bgcolor: "#e6fafc",
        border: `1px solid ${FN.chipTypeBg}`,
        fontFamily: FN.fontFamily,
        fontSize: 12,
        fontWeight: 400,
        color: FN.pressed,
        letterSpacing: "0.03px",
        lineHeight: "16px",
      }}
    >
      {label}
    </Box>
  );
}

function LocationCell({ value }: { value: string }) {
  return (
    <Typography
      sx={{
        fontFamily: FN.fontFamily,
        fontSize: 12,
        fontWeight: 400,
        color: FN.figmaAccent,
        letterSpacing: "0.03px",
        lineHeight: "16px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value || "—"}
    </Typography>
  );
}

function SortableHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSortChange,
  width,
  borderRight = true,
}: {
  label: string;
  column: LinkedFacilitySortKey;
  sortKey: LinkedFacilitySortKey;
  sortDir: "asc" | "desc";
  onSortChange: (key: LinkedFacilitySortKey) => void;
  width: string;
  borderRight?: boolean;
}) {
  const active = sortKey === column;
  return (
    <TableCell
      sx={{
        ...headerCellSx,
        width,
        borderRight: borderRight ? headerCellSx.borderRight : "none",
      }}
      sortDirection={active ? sortDir : false}
    >
      <TableSortLabel
        active={active}
        direction={active ? sortDir : "asc"}
        onClick={() => onSortChange(column)}
        sx={{
          color: "#fff !important",
          "& .MuiTableSortLabel-icon": { color: "#fff !important" },
          "&.Mui-active": { color: "#fff !important" },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

export default function FacilityNetworkChildrenTable({
  rows,
  filteredCount,
  sortKey,
  sortDir,
  onSortChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewDashboard,
  onRemove,
  loading = false,
}: Props) {
  const showAll = rowsPerPage <= LINKED_FACILITY_SHOW_ALL;
  const {
    pageRows,
    totalPages,
    page: safePage,
  } = paginateLinkedFacilityRows(rows, page, rowsPerPage, showAll);
  const rangeStart =
    filteredCount === 0 ? 0 : showAll ? 1 : (safePage - 1) * rowsPerPage + 1;
  const rangeEnd = showAll
    ? filteredCount
    : Math.min(safePage * rowsPerPage, filteredCount);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          fontWeight: 400,
          color: FN.figmaAccent,
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        {filteredCount} facilit{filteredCount === 1 ? "y" : "ies"}
      </Typography>

      <Box
        sx={{
          border: `1px solid ${FN.primary}`,
          borderRadius: "8px",
          bgcolor: "#fff",
          /* inset edge stays visible when overflow+radius clips outer border */
          boxShadow: `inset 0 0 0 1px ${FN.primary}`,
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            overflowX: "auto",
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Table
            size="small"
            sx={{
              width: "100%",
              minWidth: 720,
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
              "& .MuiTableCell-root": { boxSizing: "border-box" },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: FN.primary, height: 40 }}>
                <SortableHeader
                  label="Facility"
                  column="facility"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSortChange={onSortChange}
                  width="28%"
                />
                <SortableHeader
                  label="Type"
                  column="type"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSortChange={onSortChange}
                  width="12%"
                />
                <SortableHeader
                  label="Location"
                  column="location"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSortChange={onSortChange}
                  width="16%"
                />
                <SortableHeader
                  label="Status"
                  column="status"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSortChange={onSortChange}
                  width="14%"
                />
                <TableCell sx={{ ...headerCellSx, width: "18%" }} />
                <TableCell
                  sx={{ ...headerCellSx, width: "12%", borderRight: "none" }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={`skel-${idx}`} sx={{ height: 48 }}>
                      {Array.from({ length: 6 }).map((__, cell) => (
                        <TableCell key={cell} sx={bodyCellSx}>
                          <Skeleton variant="rounded" height={16} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : pageRows.map((row) => {
                    const canAct =
                      row.status === "linked" && row.facilityId != null;
                    return (
                      <TableRow
                        key={row.key}
                        sx={{ bgcolor: "#fff", height: 48 }}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: FN.fontFamily,
                                fontSize: 12,
                                fontWeight: 600,
                                color: FN.figmaNeutral,
                                letterSpacing: "0.03px",
                                lineHeight: "16px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: FN.fontFamily,
                                fontSize: 12,
                                fontWeight: 400,
                                color: FN.figmaAccent,
                                letterSpacing: "0.03px",
                                lineHeight: "16px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.ccnLabel} · {row.npiLabel}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <TypeCell label={row.typeLabel} />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <LocationCell value={row.location} />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <StatusPill row={row} />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                          {canAct && onViewDashboard ? (
                            <Link
                              component="button"
                              type="button"
                              underline="none"
                              onClick={() =>
                                onViewDashboard(row.facilityId as number)
                              }
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: 12,
                                fontWeight: 400,
                                color: FN.primary,
                                fontFamily: FN.fontFamily,
                                letterSpacing: "0.03px",
                                lineHeight: "16px",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              View dashboard
                              <FacilityNetworkFigmaIcon
                                src={chevronTeal}
                                size={12}
                              />
                            </Link>
                          ) : null}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...bodyCellSx,
                            borderRight: "none",
                            textAlign: "center",
                          }}
                        >
                          {canAct && onRemove ? (
                            <Link
                              component="button"
                              type="button"
                              underline="none"
                              onClick={() => onRemove(row.facilityId as number)}
                              sx={{
                                fontSize: 12,
                                fontWeight: 400,
                                color: FN.figmaError,
                                fontFamily: FN.fontFamily,
                                letterSpacing: "0.03px",
                                lineHeight: "16px",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              Remove
                            </Link>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </Box>

        {!loading && filteredCount > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "4px 8px",
              borderTop: "1px solid #e5f5f8",
              gap: "4px",
              flexShrink: 0,
              backgroundColor: "#ffffff",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                py: 0.5,
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.pressed,
                }}
              >
                Showing {rangeStart}-{rangeEnd} of {filteredCount}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  ml: "auto",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.pressed,
                  }}
                >
                  Rows per page
                </Typography>
                <Select
                  size="small"
                  value={showAll ? LINKED_FACILITY_SHOW_ALL : rowsPerPage}
                  onChange={(e) => {
                    onRowsPerPageChange(Number(e.target.value));
                    onPageChange(1);
                  }}
                  sx={{
                    minWidth: 64,
                    height: 28,
                    "& .MuiSelect-select": {
                      padding: "4px 8px",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .MuiMenuItem-root": {
                          fontSize: 12,
                          minHeight: "28px",
                          lineHeight: "28px",
                          fontFamily: FN.fontFamily,
                        },
                      },
                    },
                  }}
                >
                  {LINKED_FACILITY_ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                  <MenuItem value={LINKED_FACILITY_SHOW_ALL}>All</MenuItem>
                </Select>
                {showAll ? null : (
                    <Pagination
                      count={Math.max(totalPages, 1)}
                      page={safePage}
                      onChange={(_, value) => onPageChange(value)}
                      color="primary"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontSize: 12,
                          height: 28,
                          minWidth: 28,
                          padding: "0 6px",
                        },
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: FN.primary,
                          color: "#ffffff",
                        },
                        "& .MuiPaginationItem-root.Mui-selected:hover": {
                          backgroundColor: FN.primary,
                        },
                      }}
                    />
                )}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
